import { Link } from "react-router-dom";
import { ArrowRight, Download, Clock, Brain, Eye, Volume2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import NewsletterForm from "@/components/NewsletterForm";

const Resources = () => {
  const resources = [
    {
      id: "gentle-orientation",
      title: "A Gentle Orientation",
      subtitle: "15 Foundational Principles",
      description: "A grounded guide to regulation, coherence, and capacity. This is not a manual to study — let the words land slowly.",
      duration: "10 min read",
      type: "Guide",
      icon: Sparkles,
      href: "/somalume-gentle-orientation.html",
      featured: true
    },
    {
      id: "attention-ritual",
      title: "The Attention Ritual",
      subtitle: "A 3-5 Minute Practice",
      description: "A simple practice for restoring coherent attention. Four phases: Clear the Sky, Coherent Charging, The Single Strike, and After-Strike Integration.",
      duration: "3-5 min practice",
      type: "Practice",
      icon: Brain,
      href: "/attention-ritual.html",
      featured: false
    },
    {
      id: "environmental-audit",
      title: "Environmental Landscaping",
      subtitle: "Sensory Audit Workbook",
      description: "A self-audit tool for curating your environment across all five senses. What you surround yourself with shapes who you become.",
      duration: "20 min exercise",
      type: "Workbook",
      icon: Eye,
      href: "/environmental-landscaping.html",
      featured: false
    },
    {
      id: "lightning-metaphor",
      title: "The Lightning Metaphor",
      subtitle: "Understanding Attention",
      description: "A framework for understanding how attention works: the brain as Earth, the skull as ionosphere, and attention as the grounding strike.",
      duration: "5 min read",
      type: "Framework",
      icon: Sparkles,
      href: "/lightning-metaphor.html",
      featured: false
    }
  ];

  const featured = resources.find(r => r.featured);
  const others = resources.filter(r => !r.featured);

  return (
    <div className="min-h-screen pt-24 pb-32" data-testid="resources-page">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="hero-glow top-0 left-1/4" />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10">
          <span className="text-gold text-xs uppercase tracking-widest font-body mb-4 block">Free Tools</span>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-[#EAEAEA] leading-[0.95] mb-8">
            Resources
          </h1>
          <p className="text-xl text-[#A1A1AA] leading-relaxed">
            Practical tools for regulation, coherence, and capacity. No email required — 
            just open, use, and return when needed.
          </p>
        </div>
      </section>

      {/* Featured Resource */}
      {featured && (
        <section className="py-16 bg-paper">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="p-8 md:p-12 border border-gold/30 rounded-sm bg-gradient-to-br from-gold/10 to-transparent">
              <div className="flex flex-col md:flex-row md:items-start gap-8">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-sm border border-gold/30 bg-gold/10 flex items-center justify-center">
                    <featured.icon size={32} className="text-gold" />
                  </div>
                </div>
                <div className="flex-grow">
                  <span className="text-gold text-xs uppercase tracking-widest mb-2 block">{featured.type} • {featured.duration}</span>
                  <h2 className="font-heading text-3xl text-[#EAEAEA] mb-2">{featured.title}</h2>
                  <p className="text-gold text-sm mb-4">{featured.subtitle}</p>
                  <p className="text-[#A1A1AA] leading-relaxed mb-6">{featured.description}</p>
                  <a 
                    href={featured.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-12 px-8 uppercase text-xs tracking-widest font-medium transition-colors duration-300"
                    data-testid={`resource-${featured.id}-btn`}
                  >
                    <Download size={16} />
                    Read Now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Other Resources */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-gold text-xs uppercase tracking-widest font-body mb-4 block">Practical Tools</span>
            <h2 className="font-heading text-3xl text-[#EAEAEA]">Practices & Frameworks</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {others.map((resource) => (
              <div 
                key={resource.id}
                className="p-6 border border-white/5 rounded-sm hover:border-gold/20 transition-all duration-500 bg-paper group"
                data-testid={`resource-card-${resource.id}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-sm border border-gold/20 bg-gold/5 flex items-center justify-center group-hover:bg-gold/10 transition-colors duration-300">
                    <resource.icon size={20} className="text-gold" />
                  </div>
                  <div>
                    <span className="text-gold text-xs uppercase tracking-widest">{resource.type}</span>
                    <p className="text-[#A1A1AA] text-xs flex items-center gap-1 mt-1">
                      <Clock size={12} />
                      {resource.duration}
                    </p>
                  </div>
                </div>
                
                <h3 className="font-heading text-xl text-[#EAEAEA] mb-1">{resource.title}</h3>
                <p className="text-gold text-sm mb-3">{resource.subtitle}</p>
                <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{resource.description}</p>
                
                <a 
                  href={resource.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-gold text-sm uppercase tracking-widest group-hover:gap-3 transition-all duration-300"
                  data-testid={`resource-${resource.id}-link`}
                >
                  Open Resource
                  <ArrowRight size={14} />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Use */}
      <section className="py-20 bg-paper">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-3xl text-[#EAEAEA] mb-8 text-center">How to Use These Resources</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full border border-gold/30 bg-gold/5 flex items-center justify-center">
                <span className="font-heading text-gold text-lg">1</span>
              </div>
              <h3 className="font-heading text-lg text-[#EAEAEA] mb-2">Start Slowly</h3>
              <p className="text-[#A1A1AA] text-sm">Don't study. Let the words land. Return when something calls you back.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full border border-gold/30 bg-gold/5 flex items-center justify-center">
                <span className="font-heading text-gold text-lg">2</span>
              </div>
              <h3 className="font-heading text-lg text-[#EAEAEA] mb-2">Practice Gently</h3>
              <p className="text-[#A1A1AA] text-sm">Small, repeated regulation creates more change than dramatic experiences.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full border border-gold/30 bg-gold/5 flex items-center justify-center">
                <span className="font-heading text-gold text-lg">3</span>
              </div>
              <h3 className="font-heading text-lg text-[#EAEAEA] mb-2">Trust the Process</h3>
              <p className="text-[#A1A1AA] text-sm">Coherence returns on its own when conditions are right. Your job is to create space.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA to Books */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl text-[#EAEAEA] mb-4">Go Deeper</h2>
          <p className="text-[#A1A1AA] mb-8 max-w-xl mx-auto">
            These resources offer a taste. The books contain the complete architecture — 
            every detail, every protocol, every map.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/library">
              <Button className="bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-14 px-10 uppercase text-xs tracking-widest btn-shine" data-testid="resources-library-btn">
                Explore the Library
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <Link to="/philosophy">
              <Button variant="outline" className="border-white/20 text-[#EAEAEA] hover:bg-white/5 hover:border-gold/30 rounded-none h-14 px-10 uppercase text-xs tracking-widest">
                The 8 Pillars
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 bg-paper">
        <div className="max-w-2xl mx-auto px-6 lg:px-8">
          <NewsletterForm />
        </div>
      </section>
    </div>
  );
};

export default Resources;
